import { flash, SubmitButton } from "@xola/ui-kit";
import { getSellerLocale } from "common";
import { isEqual, omit } from "lodash";
import React, { useMemo } from "react";
import { useMutation, useQueryClient } from "react-query";
import { LanguagePreference, Permission, Seller, SupportedLanguage } from "types";
import { api } from "@/api/api";
import { Form } from "@/components/Common/Form";
import { IsGranted } from "@/components/Common/IsGranted";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { LanguageFormContent } from "@/components/Settings/Languages/LanguageFormContent";
import { TranslationLanguages } from "@/components/Settings/Languages/TranslationLanguages";
import { config } from "@/config";
import { useSeller, useUser } from "@/hooks/useAccount";
import { useInvalidate } from "@/hooks/useInvalidation";
import { getLocale } from "@/models/User";

export interface PreferenceLanguage {
    locale: string;
    name: string;
    draft?: boolean;
}

export interface LanguagePreferenceData {
    locale?: string;
    accountLocale: string;
    supportedLanguages: PreferenceLanguage[];
}

export const LanguageSettings = () => {
    const user = useUser();
    const seller = useSeller();
    const locale = getLocale(user);
    const accountLocale = getSellerLocale(seller);

    const invalidate = useInvalidate();
    const queryClient = useQueryClient();

    const { mutateAsync: updateLocale } = useMutation(async (locale?: string) => {
        return api.patch(`/users/${user.id}`, { locale: locale ?? "" });
    });

    const { mutateAsync: updateLanguagePreference } = useMutation(async (data: LanguagePreference) => {
        return api.patch(`/users/${seller.id}/preferences/language`, data);
    });

    const handleFormSubmit = async (values: LanguagePreferenceData) => {
        try {
            if (locale !== values.locale) {
                await updateLocale(values.locale);
                queryClient.invalidateQueries("account");
            }

            if (isLanguagePreferenceUpdated(seller, values)) {
                await updateLanguagePreference({
                    ...seller.preferences.language,
                    locale: values.accountLocale,
                    supportedLanguages: values.supportedLanguages.map((language) => omit(language, "draft")),
                });
                invalidate("sellers", seller.id);
            }

            flash.success("Language settings updated");
        } catch {
            flash.danger("Error while updating language settings");
        }
    };

    const initialValues = useMemo(() => {
        const supportedLanguages = seller.preferences.language?.supportedLanguages ?? [];

        return {
            locale,
            accountLocale,
            supportedLanguages: filterLanguages(supportedLanguages, accountLocale),
        };
    }, [locale, accountLocale, seller.preferences.language?.supportedLanguages]);

    return (
        <ContentLayout isWide>
            <XolaFormik<LanguagePreferenceData>
                enableReinitialize
                initialValues={initialValues}
                onSubmit={handleFormSubmit}
            >
                {({ handleSubmit, isSubmitting, dirty, handleReset }) => (
                    <Form>
                        <div className="divide-y divide-gray-lighter">
                            {config.localize.enabled && (
                                <ContentLayout.Section title="Language" description="Select your preferred language.">
                                    <LanguageFormContent />
                                </ContentLayout.Section>
                            )}

                            <IsGranted permissions={Permission.MULTI_LANGUAGE_SUPPORT_VIEW}>
                                <ContentLayout.Section
                                    title="Translations"
                                    description="Manage content translations for the languages your customers can choose at checkout"
                                >
                                    <TranslationLanguages />
                                </ContentLayout.Section>
                            </IsGranted>
                        </div>

                        <ContentLayout.Footer preferenceSidebar>
                            <SubmitButton
                                disabled={!dirty}
                                type="submit"
                                isLoading={isSubmitting}
                                onClick={handleSubmit}
                            >
                                Save
                            </SubmitButton>
                            <SecondaryButton type="reset" disabled={isSubmitting} onClick={handleReset}>
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>
                    </Form>
                )}
            </XolaFormik>
        </ContentLayout>
    );
};

const filterLanguages = (languages: SupportedLanguage[], accountLocale: string) => {
    if (!languages) {
        return [];
    }

    return languages.filter((language) => {
        return language.locale !== accountLocale.slice(0, 2);
    });
};

const isLanguagePreferenceUpdated = (seller: Seller, values: LanguagePreferenceData) => {
    const accountLocale = getSellerLocale(seller);
    const supportedLanguages = filterLanguages(seller.preferences.language.supportedLanguages, accountLocale);

    return values.accountLocale !== accountLocale || !isEqual(supportedLanguages, values.supportedLanguages);
};
